import type { IObservableArray } from 'mobx';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Campaign, DisplayCreative } from '@feathr/blackbox';
import type { IUsePoofResult } from '@feathr/components';
import {
  useDuplicateCreative,
  useRemoveCreative,
} from '@feathr/extender/components/Creatives/Creatives.hooks';
import { syncAndValidateBannersnackCreative } from '@feathr/extender/components/Creatives/Creatives.utils';
import { useToggle } from '@feathr/hooks';

export interface IUseAdWizardCreativesStepParams {
  campaign: Campaign;
  creatives: IObservableArray<DisplayCreative>;
  /** Poof animation state from the parent (drives the appear/remove transitions). */
  poof: IUsePoofResult;
}

export interface IUseAdWizardCreativesStepResult {
  /** Whether the CreativeModal is open. */
  isCreativeModalOpen: boolean;
  /** The creative currently selected in the CreativeModal carousel (or the one being edited). */
  selectedCreative: DisplayCreative | undefined;
  /** The creative pending a delete confirmation (drives the ConfirmModal). */
  creativeToDelete: DisplayCreative | undefined;
  /** Whether the Bannersnack editor modal is open. */
  isBannersnackEditorOpen: boolean;
  /** Whether the Bannersnack creative is syncing after the editor closes. */
  isBannersnackSyncing: boolean;
  /** Toggles the CreativeModal open/closed. */
  toggleCreativeModal: () => void;
  /** Selects a creative by id (used by the CreativeModal's thumbnail carousel). */
  handleSelectCreative: (id: string) => void;
  /** Opens the CreativeModal with the given creative selected. */
  handleEditCreative: (creative: DisplayCreative) => void;
  /** Clones the creative into the same campaign and animates the new entry in. */
  handleDuplicateCreative: (creative: DisplayCreative) => void;
  /** Stages a creative for deletion (opens the ConfirmModal). */
  handleDeleteCreative: (creative: DisplayCreative) => void;
  /** Confirms the pending delete and clears the staging slot. */
  handleConfirmDelete: () => void;
  /** Dismisses the delete confirmation without removing anything. */
  handleCancelDelete: () => void;
  /** Closes the CreativeModal and opens the Bannersnack editor. */
  handleEditInBannersnack: () => void;
  /** Syncs the Bannersnack creative, closes the editor, and reopens the CreativeModal. */
  handleBannersnackEditorDone: () => Promise<void>;
  /** Closes the Bannersnack editor and reopens the CreativeModal without syncing. */
  handleBannersnackEditorCancel: () => void;
}

/**
 * Encapsulates the CreativeModal / ConfirmModal / BannersnackEditor state for
 * AdWizardCreativesStep. The component only renders; this hook owns the modal
 * orchestration and delegates to the shared `useDuplicateCreative` and
 * `useRemoveCreative` hooks.
 *
 * @example
 * const {
 *   isCreativeModalOpen,
 *   selectedCreative,
 *   handleEditCreative,
 *   handleDuplicateCreative,
 *   ...
 * } = useAdWizardCreativesStep({ campaign, creatives, poof });
 */
export function useAdWizardCreativesStep({
  campaign,
  creatives,
  poof,
}: IUseAdWizardCreativesStepParams): IUseAdWizardCreativesStepResult {
  const { t } = useTranslation();
  const { animateAppear, animateRemove } = poof;

  const [isCreativeModalOpen, toggleCreativeModal] = useToggle(false);
  const [selectedCreative, setSelectedCreative] = useState<DisplayCreative | undefined>();
  const [creativeToDelete, setCreativeToDelete] = useState<DisplayCreative | undefined>();
  const [isBannersnackEditorOpen, setIsBannersnackEditorOpen] = useState(false);
  const [isBannersnackSyncing, setIsBannersnackSyncing] = useState(false);

  const { duplicateCreative } = useDuplicateCreative({
    campaignId: campaign.id,
    eventId: campaign.get('event'),
    creatives,
    onAppear: animateAppear,
  });

  const { removeCreative } = useRemoveCreative({
    creatives,
    onAnimateRemove: animateRemove,
  });

  const handleSelectCreative = useCallback(
    (id: string): void => {
      const creative = creatives.find((c) => c.id === id);
      setSelectedCreative(creative);
    },
    [creatives],
  );

  const handleEditCreative = useCallback(
    (creative: DisplayCreative): void => {
      setSelectedCreative(creative);
      toggleCreativeModal();
    },
    [toggleCreativeModal],
  );

  const handleDuplicateCreative = useCallback(
    (creative: DisplayCreative): void => {
      duplicateCreative(creative.id, `${creative.name} (copy)`);
    },
    [duplicateCreative],
  );

  const handleDeleteCreative = useCallback((creative: DisplayCreative): void => {
    setCreativeToDelete(creative);
  }, []);

  const handleConfirmDelete = useCallback((): void => {
    if (creativeToDelete) {
      removeCreative(creativeToDelete);
      setCreativeToDelete(undefined);
    }
  }, [creativeToDelete, removeCreative]);

  const handleCancelDelete = useCallback((): void => {
    setCreativeToDelete(undefined);
  }, []);

  const handleEditInBannersnack = useCallback((): void => {
    toggleCreativeModal();
    setIsBannersnackEditorOpen(true);
  }, [toggleCreativeModal]);

  const handleBannersnackEditorDone = useCallback(async (): Promise<void> => {
    if (!selectedCreative) {
      return;
    }

    setIsBannersnackSyncing(true);
    try {
      const isValid = await syncAndValidateBannersnackCreative(selectedCreative, t);
      if (!isValid) {
        return;
      }
    } finally {
      setIsBannersnackSyncing(false);
    }

    setIsBannersnackEditorOpen(false);
    toggleCreativeModal();
  }, [selectedCreative, t, toggleCreativeModal]);

  const handleBannersnackEditorCancel = useCallback((): void => {
    setIsBannersnackEditorOpen(false);
    toggleCreativeModal();
  }, [toggleCreativeModal]);

  return {
    isCreativeModalOpen,
    selectedCreative,
    creativeToDelete,
    isBannersnackEditorOpen,
    isBannersnackSyncing,
    toggleCreativeModal,
    handleSelectCreative,
    handleEditCreative,
    handleDuplicateCreative,
    handleDeleteCreative,
    handleConfirmDelete,
    handleCancelDelete,
    handleEditInBannersnack,
    handleBannersnackEditorDone,
    handleBannersnackEditorCancel,
  };
}
